import React, { useState, useEffect, useCallback } from 'react';
import {
    BreadcrumbGroup,
    SpaceBetween,
    Button,
    ContentLayout,
    Box,
    Container,
    Header,
    Table,
    TextFilter,
    Pagination,
    Badge,
    StatusIndicator,
    Link,
    DateRangePicker,
    Select,
    Icon,
    ButtonDropdown,
    ColumnLayout,
    Multiselect,
    Alert
} from '@amzn/awsui-components-console';
import { useParams, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { getUserAtom } from '../../contexts/userContext';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { useProfile } from '../../hooks/useProfiles';
import NProgress from 'nprogress';
import './JobHistory.scss';

type JobRunStatus = 
    | 'PENDING'
    | 'GENERATING_CODE'
    | 'SUBMITTING_QUERY'
    | 'QUERY_SUBMITTED'
    | 'QUERY_RUNNING'
    | 'COMPLETED'
    | 'CODE_RETRIEVED'
    | 'FAILED'
    | 'SUBMISSION_FAILED'
    | 'QUERY_FAILED'
    | 'MONITORING_FAILED'
    | 'RETRIEVAL_FAILED'
    | 'RETRYING'
    | 'FAILED_MAX_RETRIES'
    | 'SUCCEEDED';

interface JobRun {
    jobId: string;
    runId: string;
    runType: 'NORMAL' | 'ADHOC';
    status: JobRunStatus;
    ldap?: string;
    startedAt: string;
    completedAt?: string;
    codeGenerationOutput?: {
        sqlPath?: string;
        outputLocation?: string;
    };
    errorMessage?: string;
    createdBy: string;
}

const JobHistory: React.FC = () => {
    const { id: profileId, jobId } = useParams();
    const navigate = useNavigate();
    const [authenticatedUser] = useAtom(getUserAtom);
    
    // Fetch profile data from API
    const { profile: profileData, loading: profileLoading, error: profileError } = useProfile(profileId || null);
    
    // State management
    const [jobRuns, setJobRuns] = useState<JobRun[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filteringText, setFilteringText] = useState('');
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [dateRange, setDateRange] = useState<any>(null);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [sortingColumn, setSortingColumn] = useState<any>({
        sortingField: 'startedAt',
        sortingDescending: true
    });
    
    // Job details state
    const [jobData, setJobData] = useState<{
        id: string;
        name: string;
        description?: string;
        ldap?: string;
        createdBy?: string;
        createdAt?: string;
    } | null>(null);
    
    // Fetch job details
    const fetchJobDetails = useCallback(async () => {
        if (!jobId || !authenticatedUser) return;
        
        try {
            const response = await makeApiCall(
                API_PATHS.JOBS.GET,
                'POST',
                {
                    userId: authenticatedUser.username,
                    jobId: jobId
                }
            );
            
            if (response.job) {
                setJobData({
                    id: response.job.jobId,
                    name: response.job.jobName,
                    description: response.job.description,
                    ldap: response.job.ldap,
                    createdBy: response.job.createdBy,
                    createdAt: response.job.createdAt
                });
            }
        } catch (error: any) {
            console.error('Error fetching job details:', error);
            // Don't show error for job details, just use jobId as fallback
        }
    }, [jobId, authenticatedUser]);
    
    // Fetch job runs from API
    const fetchJobRuns = useCallback(async (loadMore = false) => {
        if (!jobId || !authenticatedUser) return;
        
        setLoading(true);
        setError(null);
        NProgress.start();
        
        try {
            const response = await makeApiCall(
                API_PATHS.JOBS.LIST_RUNS,
                'POST',
                {
                    jobId: jobId,
                    limit: 50,
                    ...(loadMore && nextToken ? { nextToken } : {})
                }
            );
            
            if (response.jobRuns) {
                if (loadMore) {
                    setJobRuns(prev => [...prev, ...response.jobRuns]);
                } else {
                    setJobRuns(response.jobRuns);
                }
                
                setNextToken(response.nextToken || null);
                setHasMore(!!response.nextToken);
            }
        } catch (err: any) {
            console.error('Failed to fetch job runs:', err);
            setError(err.message || 'Failed to load job runs');
        } finally {
            setLoading(false);
            NProgress.done();
        }
    }, [jobId, authenticatedUser, nextToken]);
    
    // Initial load
    useEffect(() => {
        fetchJobDetails();
        fetchJobRuns();
    }, [jobId, authenticatedUser?.username, fetchJobDetails, fetchJobRuns]);
    
    // Refresh job runs
    const handleRefresh = () => {
        fetchJobRuns();
    };
    
    // Load more job runs
    const handleLoadMore = () => {
        if (hasMore && !loading) {
            fetchJobRuns(true);
        }
    };
    
    // Filter job runs
    const filteredRuns = jobRuns.filter(run => {
        // Text filter
        const matchesText = !filteringText || 
            run.runId.toLowerCase().includes(filteringText.toLowerCase()) ||
            run.createdBy.toLowerCase().includes(filteringText.toLowerCase()) ||
            (run.errorMessage?.toLowerCase().includes(filteringText.toLowerCase()) ?? false);
        
        // Status filter
        const matchesStatus = statusFilter.length === 0 || 
            statusFilter.includes(run.status.toLowerCase());
        
        // Date range filter
        let matchesDate = true;
        if (dateRange && dateRange.value) {
            const runDate = new Date(run.startedAt);
            const startDate = dateRange.value.startDate ? new Date(dateRange.value.startDate) : null;
            const endDate = dateRange.value.endDate ? new Date(dateRange.value.endDate) : null;
            
            if (startDate && runDate < startDate) matchesDate = false;
            if (endDate && runDate > endDate) matchesDate = false;
        }
        
        return matchesText && matchesStatus && matchesDate;
    });
    
    // Sort filtered runs
    const sortedRuns = [...filteredRuns].sort((a, b) => {
        if (!sortingColumn?.sortingField) return 0;
        
        let aValue: any;
        let bValue: any;
        
        switch (sortingColumn.sortingField) {
            case 'runId':
                aValue = a.runId;
                bValue = b.runId;
                break;
            case 'startedAt':
                aValue = new Date(a.startedAt).getTime();
                bValue = new Date(b.startedAt).getTime();
                break;
            case 'status':
                aValue = a.status;
                bValue = b.status;
                break;
            case 'runType':
                aValue = a.runType;
                bValue = b.runType;
                break;
            case 'executionTime':
                // Calculate execution time for sorting
                const aStart = new Date(a.startedAt).getTime();
                const aEnd = a.completedAt ? new Date(a.completedAt).getTime() : new Date().getTime();
                aValue = aEnd - aStart;
                
                const bStart = new Date(b.startedAt).getTime();
                const bEnd = b.completedAt ? new Date(b.completedAt).getTime() : new Date().getTime();
                bValue = bEnd - bStart;
                break;
            case 'createdBy':
                aValue = a.createdBy;
                bValue = b.createdBy;
                break;
            default:
                return 0;
        }
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // Compare values
        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;
        
        // Apply sort direction
        return sortingColumn.sortingDescending ? -comparison : comparison;
    });
    
    // Pagination
    const pageSize = 10;
    const pagesCount = Math.ceil(sortedRuns.length / pageSize);
    const currentPageItems = sortedRuns.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );
    
    // Handle sorting
    const handleSortingChange = (event: any) => {
        setSortingColumn(event.detail.sortingColumn);
    };
    
    // Format date
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch {
            return dateString;
        }
    };
    
    // Calculate execution time
    const calculateExecutionTime = (startedAt: string, completedAt?: string) => {
        try {
            const start = new Date(startedAt);
            const end = completedAt ? new Date(completedAt) : new Date();
            const diff = end.getTime() - start.getTime();
            
            const hours = Math.floor(diff / 3600000);
            const minutes = Math.floor((diff % 3600000) / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } catch {
            return '-';
        }
    };
    
    // Failure statuses
    const FAILURE_STATUSES = [
        'FAILED',
        'SUBMISSION_FAILED',
        'QUERY_FAILED',
        'MONITORING_FAILED',
        'FAILED_MAX_RETRIES',
        'RETRIEVAL_FAILED'
    ];

    const isFailureStatus = (status: string) => FAILURE_STATUSES.includes(status);

    // Status indicator component
    const StatusIcon = ({ status }: { status: string }) => {
        // Check if it's a failure status
        if (isFailureStatus(status)) {
            return <StatusIndicator type="error">{status.replace(/_/g, ' ')}</StatusIndicator>;
        }
        
        // Handle specific statuses
        switch (status) {
            case 'COMPLETED':
            case 'SUCCEEDED':
                return <StatusIndicator type="success">Completed</StatusIndicator>;
            case 'PENDING':
                return <StatusIndicator type="pending">Pending</StatusIndicator>;
            case 'GENERATING_CODE':
                return <StatusIndicator type="loading">Generating Code</StatusIndicator>;
            case 'SUBMITTING_QUERY':
                return <StatusIndicator type="loading">Submitting Query</StatusIndicator>;
            case 'QUERY_SUBMITTED':
                return <StatusIndicator type="info">Query Submitted</StatusIndicator>;
            case 'QUERY_RUNNING':
                return <StatusIndicator type="in-progress">Query Running</StatusIndicator>;
            case 'CODE_RETRIEVED':
                return <StatusIndicator type="success">Code Retrieved</StatusIndicator>;
            case 'RETRYING':
                return <StatusIndicator type="warning">Retrying</StatusIndicator>;
            default:
                return <StatusIndicator type="pending">{status}</StatusIndicator>;
        }
    };
    
    // Run type badge
    const RunTypeBadge = ({ type }: { type: string }) => {
        if (type === 'ADHOC') {
            return <Badge color="blue">Ad-hoc</Badge>;
        }
        return <Badge>Normal</Badge>;
    };
    
    // Table column definitions
    const columnDefinitions = [
        {
            id: 'runId',
            header: 'Run ID',
            cell: (item: JobRun) => (
                <Link 
                    href="#" 
                    external={false}
                    onFollow={(e) => {
                        e.preventDefault();
                        // Navigate to run details
                        navigate(`/profile/${profileId}/job/${jobId}/run/${item.runId}`);
                    }}
                >
                    {item.runId.substring(0, 8)}...
                </Link>
            ),
            sortingField: 'runId',
            isRowHeader: true,
            width: 150
        },
        {
            id: 'startedAt',
            header: 'Started At',
            cell: (item: JobRun) => formatDate(item.startedAt),
            sortingField: 'startedAt',
            width: 200
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item: JobRun) => <StatusIcon status={item.status} />,
            sortingField: 'status',
            width: 120
        },
        {
            id: 'runType',
            header: 'Run Type',
            cell: (item: JobRun) => <RunTypeBadge type={item.runType} />,
            sortingField: 'runType',
            width: 120
        },
        {
            id: 'executionTime',
            header: 'Duration',
            cell: (item: JobRun) => calculateExecutionTime(item.startedAt, item.completedAt),
            sortingField: 'executionTime',
            width: 120
        },
        {
            id: 'createdBy',
            header: 'Created By',
            cell: (item: JobRun) => (
                <Link href={`https://phonetool.amazon.com/users/${item.createdBy}`} external={true}>
                    @{item.createdBy}
                </Link>
            ),
            sortingField: 'createdBy',
            width: 150
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (item: JobRun) => (
                <Button
                    variant="link"
                    iconName="external"
                    onClick={() => navigate(`/profile/${profileId}/job/${jobId}/run/${item.runId}`)}
                >
                    View Details
                </Button>
            ),
            width: 150
        }
    ];
    
    // Handle clear filters
    const handleClearFilters = () => {
        setFilteringText('');
        setStatusFilter([]);
        setDateRange(null);
        setCurrentPageIndex(1);
    };
    
    // Status filter options
    const statusOptions = [
        { label: 'Success', value: 'success' },
        { label: 'Running', value: 'running' },
        { label: 'Failed', value: 'failed' },
        { label: 'Submitted', value: 'submitted' }
    ];
    
    // Get active filter count
    const activeFilterCount = [
        filteringText,
        statusFilter.length > 0,
        dateRange?.value
    ].filter(Boolean).length;
    
    return (
        <div className="job-history-page">
            <ContentLayout
                defaultPadding={false}
                headerVariant="high-contrast"
                header={
                    <Box padding={{ horizontal: "xxxl", vertical: "l" }}>
                        <SpaceBetween size="m" direction="vertical">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <BreadcrumbGroup
                                    items={[
                                        { text: "Home", href: "/" },
                                        { text: profileData?.name || 'Loading...', href: `/profile/${profileId}` },
                                        { text: "Jobs", href: `/profile/${profileId}/jobs` },
                                        { text: jobData?.name || jobId || 'Job Runs', href: "#" }
                                    ]}
                                    ariaLabel="Breadcrumbs"
                                />
                                <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto', alignItems: 'center' }}>
                                    {jobData?.ldap && (
                                        <Badge color="green">{jobData.ldap}</Badge>
                                    )}
                                    <Button
                                        iconName="edit"
                                        onClick={() => navigate(`/profile/${profileId}/job/${jobId}`)}
                                        variant="normal"
                                    >
                                        Provide Template Feedback
                                    </Button>
                                    <Button 
                                        iconName="refresh"
                                        onClick={handleRefresh}
                                        variant="normal"
                                        loading={loading}
                                        ariaLabel="Refresh job runs"
                                    >
                                        
                                    </Button>
                                </div>
                            </div>
                            <SpaceBetween size="xs" direction="vertical">
                                <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                                    {jobData?.name || jobId || 'Job Run History'}
                                </Box>
                                {(jobData?.createdBy || jobData?.createdAt) && (
                                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                                        {jobData?.createdBy && (
                                            <Link href={`https://phonetool.amazon.com/users/${jobData.createdBy}`} external={true}>
                                                @{jobData.createdBy}
                                            </Link>
                                        )}
                                        {jobData?.createdBy && jobData?.createdAt && (
                                            <Box color="text-body-secondary">•</Box>
                                        )}
                                        {jobData?.createdAt && (
                                            <Box color="inherit" fontSize="body-s">
                                                Created {new Date(jobData.createdAt).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </Box>
                                        )}
                                    </SpaceBetween>
                                )}
                            </SpaceBetween>
                            <Box color="inherit" fontWeight="light">
                                <em>Below table provides information on all the historic runs for this job</em>
                            </Box>
                            {jobData?.description && (
                                <Box fontSize="body-s" color="inherit">
                                    {jobData.description}
                                </Box>
                            )}
                        </SpaceBetween>
                    </Box>
                }
            >
                <Box padding={{ horizontal: "xxl", vertical: "l" }}>
                    <Container>
                        <SpaceBetween size="l" direction="vertical">
                            {/* Filters */}
                            {/* Error Alert */}
                            {error && (
                                <Alert
                                    type="error"
                                    dismissible
                                    onDismiss={() => setError(null)}
                                    header="Error loading job runs"
                                >
                                    {error}
                                </Alert>
                            )}
                            
                            {/* Filters */}
                            <div className="filters-section">
                                <SpaceBetween size="m" direction="vertical">
                                    <TextFilter
                                        filteringText={filteringText}
                                        filteringPlaceholder="Find job runs by ID, user or any other information availble below table"
                                        filteringAriaLabel="Filter job runs"
                                        onChange={({ detail }) => {
                                            setFilteringText(detail.filteringText);
                                            setCurrentPageIndex(1);
                                        }}
                                        countText={`${sortedRuns.length} matches`}
                                    />
                                    
                                    {(statusFilter.length > 0 || dateRange) && (
                                        <SpaceBetween size="s" direction="horizontal">
                                            {statusFilter.length > 0 && (
                                                <div className="status-filter-group">
                                                    <span className="filter-label">Status: {statusFilter.join(', ')}</span>
                                                    <Button
                                                        iconName="close"
                                                        variant="inline-icon"
                                                        ariaLabel="Remove status filter"
                                                        onClick={() => setStatusFilter([])}
                                                    />
                                                </div>
                                            )}
                                            
                                            {statusFilter.length > 0 && dateRange && (
                                                <span className="filter-separator">and</span>
                                            )}
                                            
                                            {dateRange && (
                                                <div className="date-filter-group">
                                                    <span className="filter-label">
                                                        Date: {dateRange.value?.startDate || ''} - {dateRange.value?.endDate || ''}
                                                    </span>
                                                    <Button
                                                        iconName="close"
                                                        variant="inline-icon"
                                                        ariaLabel="Remove date filter"
                                                        onClick={() => setDateRange(null)}
                                                    />
                                                </div>
                                            )}
                                            
                                            {activeFilterCount > 0 && (
                                                <Button
                                                    onClick={handleClearFilters}
                                                    variant="link"
                                                >
                                                    Clear all filters
                                                </Button>
                                            )}
                                        </SpaceBetween>
                                    )}
                                </SpaceBetween>
                            </div>
                            
                            {/* Table */}
                            <Table
                                columnDefinitions={columnDefinitions}
                                items={currentPageItems}
                                loading={loading}
                                loadingText="Loading job runs"
                                onSortingChange={handleSortingChange}
                                sortingColumn={sortingColumn}
                                trackBy="runId"
                                variant="container"
                                stickyHeader={true}
                                empty={
                                    <Box textAlign="center" color="text-status-inactive" padding={{ vertical: 'xxl' }}>
                                        <SpaceBetween size="m">
                                            <Icon name="search" size="large" />
                                            <b>No job runs found</b>
                                            <Box>No runs match the current filters</Box>
                                        </SpaceBetween>
                                    </Box>
                                }
                                header={
                                    <Header
                                        counter={`(${sortedRuns.length})`}
                                        actions={
                                            <Pagination
                                                currentPageIndex={currentPageIndex}
                                                pagesCount={Math.max(1, pagesCount)}
                                                onChange={({ detail }) =>
                                                    setCurrentPageIndex(detail.currentPageIndex)
                                                }
                                                ariaLabels={{
                                                    nextPageLabel: 'Next page',
                                                    previousPageLabel: 'Previous page',
                                                    pageLabel: pageNumber => `Page ${pageNumber} of all pages`
                                                }}
                                            />
                                        }
                                    >
                                        Job Run History
                                    </Header>
                                }
                            />
                            
                            {/* Load More Button */}
                            {hasMore && !loading && (
                                <Box textAlign="center">
                                    <Button
                                        onClick={handleLoadMore}
                                        loading={loading}
                                        loadingText="Loading more..."
                                    >
                                        Load more job runs
                                    </Button>
                                </Box>
                            )}
                        </SpaceBetween>
                    </Container>
                    {/* Add spacing at bottom to prevent footer overlap */}
                    <Box padding={{ bottom: "xxxl" }} />
                </Box>
            </ContentLayout>
        </div>
    );
};

export default JobHistory;
